import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import BackgroundImage from '../../../BackgroundImage';
import { getInitials } from './helpers';
import { META_SEPARATOR, OPPONENT_LINK_TARGET, OPPONENT_LINK_REL } from './constants';
import styles from './playersList.scss';

// Avatar + teal name link → profile + `M/F • Location`, shared by the desktop row and the mobile card.
// With no profile image, the avatar falls back to uppercase initials on a black circle (the app's
// standard placeholder treatment) rather than a generic silhouette.
const PlayerIdentity = ({ player, 'data-testid': dataTestId }) => {
    const { id, displayName, gender, location, playerProfileImages } = player;
    const meta = [gender, location].filter(Boolean).join(META_SEPARATOR);
    // Only treat the player as having an image when BackgroundImage would actually render one — a real
    // `default` src or a complete `icon` srcset. A present-but-empty images object (all-null fields)
    // must fall through to the initials placeholder, not paint a blank circle.
    const icon = playerProfileImages?.icon;
    const hasCompleteIcon = !!(icon?.oneX && icon?.twoX && icon?.threeX);
    const hasImage = !!(playerProfileImages?.default || hasCompleteIcon);

    return (
        <>
            <div className={styles.avatar}>
                {hasImage ? (
                    <BackgroundImage
                        baseUrl={'/v1/player/'}
                        images={playerProfileImages}
                        imageType={'icon'}
                        parentStylesClass={styles.avatarImage}
                    />
                ) : (
                    <div className={styles.avatarInitials} data-testid={dataTestId && `${dataTestId}.avatar-initials`}>
                        {getInitials(player)}
                    </div>
                )}
            </div>
            <div className={styles.nameInfo}>
                <Link
                    to={`/players/${id}`}
                    target={OPPONENT_LINK_TARGET}
                    rel={OPPONENT_LINK_REL}
                    className={styles.playerName}
                    data-testid={dataTestId && `${dataTestId}.name`}
                >
                    {displayName}
                </Link>
                {meta && (
                    <span className={styles.playerMeta} data-testid={dataTestId && `${dataTestId}.meta`}>
                        {meta}
                    </span>
                )}
            </div>
        </>
    );
};

PlayerIdentity.propTypes = {
    player: PropTypes.instanceOf(Object).isRequired,
    'data-testid': PropTypes.string,
};

PlayerIdentity.defaultProps = {
    'data-testid': null,
};

export default PlayerIdentity;
